/**
 * Cross-window replay relay for `@suite/tour`.
 *
 * A replay row inside the SEPARATE settings window (`?view=settings`) cannot
 * start the tour meaningfully: the overlay would render over the bare
 * two-column shell where none of the step targets exist. It instead REQUESTS
 * the replay — a same-origin broadcast the application's main-window provider
 * answers by starting its registered tour (TourContext) — and closes the
 * window.
 *
 * Two delivery paths, deliberately redundant:
 *   - BroadcastChannel (instant, no persistence);
 *   - a localStorage `storage` event (fires in every OTHER same-origin
 *     document, covering webviews where channels misbehave).
 * The writer never hears itself through either path. Embedded panes ignore
 * requests (the guard lives in TourContext) — the tour is a
 * main-application surface.
 */

const CHANNEL_NAME = "suite.tour.replay";
const KEY_PREFIX = "suite.tour.";
const KEY_SUFFIX = ".replay";

/** The localStorage key one application's replay request travels on. */
export function replayRequestKey(appId: string): string {
  return `${KEY_PREFIX}${appId}${KEY_SUFFIX}`;
}

let channel: BroadcastChannel | null = null;

function broadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      return null;
    }
  }
  return channel;
}

/**
 * Ask the application's main window to replay its tour. Called from a
 * settings window's replay row (with `useTourReplayEntry`), right before the
 * window closes itself.
 */
export function requestTourReplay(appId: string): void {
  // The nonce keeps consecutive requests distinct — a storage event only
  // fires when the written value actually changes.
  const message = { appId, at: Date.now(), n: Math.random() };
  try {
    window.localStorage.setItem(replayRequestKey(appId), JSON.stringify(message));
  } catch {
    // Storage refused — the channel below still delivers.
  }
  try {
    broadcastChannel()?.postMessage(message);
  } catch {
    // No channel — the storage event remains.
  }
}

/**
 * Listen for replay requests for ANY application id; the caller (the tour
 * provider) decides which ones it owns. Returns the unsubscribe function.
 */
export function subscribeTourReplay(handler: (appId: string) => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (!event.key || !event.newValue) return;
    if (!event.key.startsWith(KEY_PREFIX) || !event.key.endsWith(KEY_SUFFIX)) return;
    const appId = event.key.slice(KEY_PREFIX.length, event.key.length - KEY_SUFFIX.length);
    if (appId) handler(appId);
  };
  window.addEventListener("storage", onStorage);
  const onMessage = (event: MessageEvent) => {
    const data = event.data as { appId?: unknown } | null;
    if (!data || typeof data.appId !== "string") return;
    handler(data.appId);
  };
  const ch = broadcastChannel();
  ch?.addEventListener("message", onMessage);
  return () => {
    window.removeEventListener("storage", onStorage);
    ch?.removeEventListener("message", onMessage);
  };
}
